/**
 * Launch — the pre-start checklist (stage `ready_to_start`).
 *
 * GitHub-merge-box-shaped: per-assignment readiness rows (NP · fee ·
 * due · locked), team/config, the Backoffice project created at the
 * Ready transition, and — for activation configs — the "client can also
 * start this" chip. The gate strip owns the Start button.
 */

import { useEffect, useState, type ReactNode } from 'react';

import { Anchor, Badge, Group, Loader, Stack, Text } from '@mantine/core';
import { IconCheck, IconExternalLink, IconX } from '@tabler/icons-react';

import { NpAssignmentsProvider, useNpAssignmentsContext } from '@/components/NpAssignments';
import { config } from '@/config';
import { vnextApiClient } from '@/services/vnext-api';
import type { ReportRequestDetail } from '@/types/report-request';

interface LaunchSectionProps {
  detail: ReportRequestDetail;
  teamName: string | null;
}

export function LaunchSection({ detail, teamName }: Readonly<LaunchSectionProps>) {
  return (
    <NpAssignmentsProvider reportId={detail.reportId} reportRequestId={detail.id}>
      <LaunchChecklist detail={detail} teamName={teamName} />
    </NpAssignmentsProvider>
  );
}

function ChecklistRow({
  ok,
  label,
  detail,
}: Readonly<{ ok: boolean; label: string; detail?: ReactNode }>) {
  return (
    <Group gap="xs" wrap="nowrap">
      {ok ? (
        <IconCheck size={16} color="var(--mantine-color-green-6)" />
      ) : (
        <IconX size={16} color="var(--mantine-color-red-6)" />
      )}
      <Text size="sm">{label}</Text>
      {detail && (
        <Text size="xs" c="dimmed">
          {detail}
        </Text>
      )}
    </Group>
  );
}

function LaunchChecklist({
  detail,
  teamName,
}: Readonly<{ detail: ReportRequestDetail; teamName: string | null }>) {
  const { assignments, isLoading } = useNpAssignmentsContext();
  const [backofficeProjectId, setBackofficeProjectId] = useState<string | null>(null);

  // The Backoffice project id lives on the Report row (created DRAFT at
  // the Ready transition); the RR detail doesn't carry it.
  useEffect(() => {
    let cancelled = false;
    if (!detail.reportId) {
      return;
    }
    void (async () => {
      try {
        const report = await vnextApiClient.getReport(detail.reportId as string);
        if (!cancelled) {
          setBackofficeProjectId(report.backoffice_project_id ?? null);
        }
      } catch {
        // The link row simply doesn't render — Start stays available.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [detail.reportId]);

  const pendingActivation = detail.reportLifecycleStage === 'pending_activation';

  return (
    <Stack gap="sm">
      <Text size="sm" c="dimmed">
        Everything below was checked when this request reached Start. Press Start (above) to
        dispatch it.
      </Text>

      <ChecklistRow ok={!!detail.teamId} label="Team" detail={teamName ?? detail.teamId ?? '—'} />
      <ChecklistRow
        ok={!!detail.reportConfigId}
        label="Report config"
        detail={detail.configName ?? detail.reportConfigId ?? '—'}
      />
      <ChecklistRow
        ok={(detail.subjectStrings?.length ?? 0) > 0}
        label="Subjects"
        detail={`${detail.subjectStrings?.length ?? 0} subject(s)`}
      />

      {isLoading && assignments.length === 0 ? (
        <Group gap="xs">
          <Loader size="xs" />
          <Text size="xs" c="dimmed">
            Loading assignments…
          </Text>
        </Group>
      ) : (
        assignments.map((a, idx) => {
          const np = a.networkPartner?.name ?? a.networkPartner?.email ?? 'no partner';
          const due =
            a.npDueDate ??
            (a.turnaroundTimeDays != null ? `${a.turnaroundTimeDays}d turnaround` : null);
          return (
            <ChecklistRow
              key={a.id}
              ok={a.isReady}
              label={`Assignment A${idx + 1}`}
              detail={`${np}${a.npFee != null ? ` · fee ${a.npFee}` : ''}${due ? ` · ${due}` : ''}`}
            />
          );
        })
      )}

      {backofficeProjectId && (
        <ChecklistRow
          ok
          label="Backoffice project created (draft)"
          detail={
            <Anchor
              href={`${config.backofficeAdminUrl}/admin/project/${backofficeProjectId}`}
              target="_blank"
              rel="noreferrer"
              size="xs"
              data-testid="backoffice-project-link"
            >
              <Group gap={4} wrap="nowrap">
                #{backofficeProjectId} — activates on Start
                <IconExternalLink size={11} />
              </Group>
            </Anchor>
          }
        />
      )}

      {pendingActivation && (
        <Badge color="grape" variant="light">
          Waiting for client activation — the client can also start this report
        </Badge>
      )}
    </Stack>
  );
}
